/**
 * What the server's field permission map (path → { visibility, disabled })
 * allows on each profile tile.
 *
 * @param {Object<string, {visibility: boolean, disabled: boolean}>} [fields]
 * @param {(name: string[]) => string|null} sectionOf - tile of a form path;
 *   null for a path no tile shows
 * @returns {Object<string, {visible: boolean, editable: boolean}>} only tiles
 *   that have at least one path in the map
 */
export function tileFieldAccess(fields, sectionOf) {
  const tiles = {};
  Object.entries(fields || {}).forEach(([path, state]) => {
    const tile = sectionOf(path.split('.'));
    if (!tile) return;
    const entry = (tiles[tile] ||= { visible: false, editable: false });
    if (state?.visibility) {
      entry.visible = true;
      if (!state.disabled) entry.editable = true;
    }
  });
  return tiles;
}

/**
 * Drops tiles the role has nothing to see on — every field of theirs hidden
 * and no content of their own besides the form — and groups left empty.
 *
 * @param {Array<{tiles: string[]}>} groups
 * @param {Object<string, {visible: boolean}>} access - from tileFieldAccess
 * @param {(id: string) => boolean} hasOwnContent
 */
export function dropEmptyTiles(groups, access, hasOwnContent) {
  return groups
    .map((g) => ({
      ...g,
      tiles: g.tiles.filter(
        (id) => !access[id] || access[id].visible || hasOwnContent(id)
      ),
    }))
    .filter((g) => g.tiles.length > 0);
}

/**
 * Whether the role may change anything on a tile. Tiles without fields in the
 * map are not decided here.
 */
export const tileEditable = (access, id) => !access[id] || access[id].editable;
